//__PAYPAL_JS

// ── SDK loader ────────────────────────────────────────────────────────────────

const PAYPAL_SDK_BASE = 'https://www.paypal.com/sdk/js'

/** Script element ID used to deduplicate SDK injection across all components. */
export const PAYPAL_SDK_SCRIPT_ID = 'paypal-sdk-script'

/** Default transaction currency. */
export const PAYPAL_CURRENCY = 'THB'

/**
 * Build the PayPal JS SDK URL with consistent defaults.
 *
 * @param clientId  NEXT_PUBLIC_PAYPAL_CLIENT_ID value
 * @param options.enableFunding   Additional funding sources, e.g. ['paylater']
 * @param options.disableFunding  Funding sources to hide, defaults to ['card']
 *                                (hides the "Credit or debit cards" button)
 */
export function buildPayPalSdkUrl(
  clientId: string,
  options: { enableFunding?: string[]; disableFunding?: string[] } = {}
): string {
  const disableFunding = options.disableFunding ?? ['card']
  const params = new URLSearchParams({
    'client-id': clientId,
    'currency': PAYPAL_CURRENCY
  })
  if (options.enableFunding?.length) {
    params.set('enable-funding', options.enableFunding.join(','))
  }
  if (disableFunding.length) {
    params.set('disable-funding', disableFunding.join(','))
  }
  return `${PAYPAL_SDK_BASE}?${params.toString()}`
}

// ─────────────────────────────────────────────────────────────────────────────

interface EGiftPayPalOrderItem {
  id: string | number
  quantity: number
}

interface EGiftPayPalData {
  formData: {
    name: string
    email: string
    phone: string
    [key: string]: any
  }
  orderItems: EGiftPayPalOrderItem[]
  promotionId: string | number
  csrf: string
  language: string
}

/**
 * Format fields for the PayPal create_order.php endpoint (eGift / promotion flow).
 */
export const formatEGiftPayPalData = ({
  formData,
  orderItems,
  promotionId,
  csrf,
  language
}: EGiftPayPalData) => {
  const payload = new URLSearchParams({
    order_type: 'eGift',
    promotion: promotionId.toString(),
    language,
    name: formData.name,
    email: formData.email,
    phone: formData.phone,
    pMethod: 'paypal',
    csrf
  })

  orderItems.forEach((item, idx) => {
    payload.append(`items[${idx}][pd_id]`, item.id.toString())
    payload.append(`items[${idx}][qty]`, item.quantity.toString())
  })

  return payload
}

interface PaymentLinkPayPalData {
  formData: {
    name: string
    email: string
    phone: string
    [key: string]: any
  }
  csrf: string
  language: string
  reference?: string
}

/**
 * Format fields for the PayPal create_order.php endpoint.
 * No cardToken or amount — both are resolved server-side.
 */
export const formatPaymentLinkPayPalData = ({
  reference,
  formData,
  csrf,
  language
}: PaymentLinkPayPalData) => {
  return new URLSearchParams({
    order_type: 'paymentLink',
    reference: reference || '',
    language,
    name: formData.name,
    email: formData.email,
    phone: formData.phone,
    pMethod: 'paypal',
    csrf
  })
}
